import React from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import { css, cssMap } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import Image from '@atlaskit/image';
import Button from '@atlaskit/button/button';
import { Box, Stack, Text } from '@atlaskit/primitives/compiled';

import { mergeLiveDocI18n } from '@confluence/live-pages-utils/entry-points/mergeLiveDocI18n.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { useCreateHomeEmptyStateDoc } from '@confluence/create-blank-fabric-page/entry-points/useCreateHomeEmptyStateDoc.ts';

import { CreatePageButton } from '../CreatePageButton.tsx';
import RecentEmptyStateDark from '../images/recent/RecentEmptyStateDark.svg';
import RecentEmptyStateLight from '../images/recent/RecentEmptyStateLight.svg';
import RecentEmptyStateMohitiDark from '../images/recent/RecentEmptyStateMohitiDark.svg';
import RecentEmptyStateMohitiLight from '../images/recent/RecentEmptyStateMohitiLight.svg';

const CREATE_DOC_SOURCE = 'recentWorkEmptyState';

const emptyStateStyles = css({
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'space-between',
	position: 'relative',
	height: '128px',
	padding: token('space.300'),
	overflow: 'hidden',
});

const headerStyles = css({
	font: token('font.heading.xsmall'),
	marginBottom: token('space.100'),
});

const contentStyles = css({
	maxWidth: '400px',
});

const imageStyles = css({
	position: 'absolute',
	right: token('space.300'),
	top: token('space.300'),
});

const listContainerStyles = css({
	paddingTop: token('space.150'),
	backgroundColor: token('elevation.surface.raised'),
	borderRadius: token('radius.large'),
	border: `${token('border.width')} solid ${token('color.border')}`,
});

const mohitiStyles = cssMap({
	banner: {
		borderRadius: token('radius.xlarge'),
		paddingBlock: token('space.250'),
		paddingInline: token('space.200'),
		textAlign: 'center',
	},
});

const i18nBase = defineMessages({
	header: {
		id: 'home.views.overview.recent-empty-state.header',
		defaultMessage: 'Get out there and explore',
		description: 'Descriptive header shown when there are not recent pages',
	},
	body: {
		id: 'home.views.overview.recent-empty-state.body',
		defaultMessage: 'Once you visit, edit, or create pages, you’ll find the most recent ones here.',
		description:
			'Descriptive body shown when there are not recent pages indicating how to make pages appear',
	},
});

const i18nLiveDocs = defineMessages({
	body: {
		id: 'home.views.overview.recent-empty-state.body.livedocs',
		defaultMessage:
			'Once you visit, edit, or create pages or live docs, you’ll find the most recent ones here.',
		description: 'Updated version of home.views.overview.recent-empty-state.body',
	},
});

const i18nMohiti = defineMessages({
	header: {
		id: 'home.views.overview.recent-empty-state.mohiti.header',
		defaultMessage: 'Start something great',
		description:
			'Header shown in the Mohiti recent-work empty state encouraging the user to create their first doc.',
	},
	body: {
		id: 'home.views.overview.recent-empty-state.mohiti.body',
		defaultMessage:
			'Kick things off with a new doc and your most recent updates will show up here.',
		description:
			'Body shown in the Mohiti recent-work empty state explaining that recent work appears once the user starts creating.',
	},
	ctaCreateDoc: {
		id: 'home.views.overview.recent-empty-state.mohiti.cta.create-doc',
		defaultMessage: 'Create doc',
		description:
			'Call-to-action button in the Mohiti recent-work empty state that creates a new live doc.',
	},
});

const i18n = mergeLiveDocI18n(i18nBase, i18nLiveDocs);

const LegacyRecentWorkEmptyState = (): React.JSX.Element => {
	return (
		<div css={listContainerStyles} data-testid="recent-work-empty-list-container">
			<div css={emptyStateStyles}>
				<div css={contentStyles}>
					<div css={headerStyles}>
						<FormattedMessage {...i18n.header} />
					</div>
					<FormattedMessage {...i18n.body} />
				</div>
				<div>
					<CreatePageButton
						appearance="default"
						creationLinkSource="home-overview-recentList"
						showCreateLivePageButton
					/>
				</div>
				<Image
					src={RecentEmptyStateLight}
					srcDark={RecentEmptyStateDark}
					// eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides
					css={imageStyles}
					alt=""
				/>
			</div>
		</div>
	);
};

const MohitiRecentWorkEmptyState = (): React.JSX.Element => {
	const { createDoc } = useCreateHomeEmptyStateDoc({ source: CREATE_DOC_SOURCE });

	return (
		<Box backgroundColor="elevation.surface.sunken" xcss={mohitiStyles.banner}>
			<Stack space="space.150" alignInline="center">
				<Stack space="space.100" alignInline="center">
					<Image
						src={RecentEmptyStateMohitiLight}
						srcDark={RecentEmptyStateMohitiDark}
						width="52"
						height="50"
						alt=""
						testId="recent-work-empty-mohiti-image"
					/>
					<Stack space="space.025" alignInline="center">
						<Text weight="bold">
							<FormattedMessage {...i18nMohiti.header} />
						</Text>
						<Text>
							<FormattedMessage {...i18nMohiti.body} />
						</Text>
					</Stack>
				</Stack>
				<Button onClick={createDoc}>
					<FormattedMessage {...i18nMohiti.ctaCreateDoc} />
				</Button>
			</Stack>
		</Box>
	);
};

export const RecentWorkEmptyState = (): React.JSX.Element => {
	if (expValEquals('cc_mohiti_for_you_empty_states_exp', 'isEnabled', true)) {
		return <MohitiRecentWorkEmptyState />;
	}

	return <LegacyRecentWorkEmptyState />;
};
